"use client";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useDispatch } from "react-redux";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/commonFunctions";
import icon from "@/components/icons/Icons";
import { apiPost } from "@/utils/apiClient";
import { useLoginModal } from "@/utils/useLoginModal";
import { useFocusTrap } from "@/utils/useFocusTrap";
import { increaseCart } from "@/redux/features/AddToCart/cartSlice";
import { addToast } from "@/components/ui/CustomToastManager";
import {
  SuitabilityApiResponse,
  SuitabilityBestReturn,
  SuitabilityQuestion,
  SuitabilitySelectedAnswer,
  SuitabilityUserDetails,
} from "@/type/commonTypes";
import SuitabilityChatBubble from "../others/SuitabilityChatBubble";
import SuitabilityTypingBubble from "../others/SuitabilityTypingBubble";
import SuitabilitySelectedCard from "../others/SuitabilitySelectedCard";
import SuitabilityQuestionOptions from "../others/SuitabilityQuestionOptions";
import SuitabilityDetailsForm from "../others/SuitabilityDetailsForm";
import SuitabilityResultCard from "../others/SuitabilityResultCard";
import SuitabilityProgressBar from "../others/SuitabilityProgressBar";

// Lottie player + animation JSON are heavy; load them only once the test is opened
const loadSuitabilityLoader = () => import("../others/SuitabilityLoader");
const SuitabilityLoader = dynamic(loadSuitabilityLoader, {
  ssr: false,
  loading: () => <div className="h-full xs:p-5 relative" />,
});

type CartAPIResponse = {
  status: boolean;
  message: string;
  cart_id: number;
};

type fundProps = {
  name: string;
  imgCode: string;
  schemeCode: string;
  schemeCatId: string;
  isin: string;
  onShowModalChange?: (value: boolean) => void;
};

const EMPTY_USER_DETAILS: SuitabilityUserDetails = { name: "", phone: "", email: "" };

const INTRO_QUESTION: SuitabilityQuestion = {
  id: -1,
  question: "Hey! I’m Rishi. I’m here to help you assess your risk profile. Shall we begin?",
  options: [
    {
      id: -11,
      label: "Yes, Let’s Begin",
      type: "start",
      img: "chatbox-user-avatar.png",
    },
    {
      id: -12,
      label: "No, I’ll do it Later",
      type: "close",
      img: "chatbox-user-avatar.png",
    },
  ],
  isIntro: true,
};

const formatQuestions = (res: SuitabilityApiResponse) => {
  const subjectKey = Object.keys(res.result)[0] || "";
  const apiQuestions = res.result[subjectKey] || [];

  const formattedQuestions: SuitabilityQuestion[] = apiQuestions.flatMap((group, groupIndex) =>
    group.map((q) => ({
      id: q.id,
      question: q.question,
      options: q.options.map((opt, optIndex) => ({
        id: opt.id,
        label: opt.option,
        type: opt.description,
        img: `${String.fromCharCode(97 + optIndex)}-q${groupIndex + 1}.svg`,
      })),
      correct: "",
    })),
  );

  return { subjectKey, questions: [INTRO_QUESTION, ...formattedQuestions] };
};

export default function SuitabilityTestModal({
  name,
  imgCode,
  schemeCode,
  schemeCatId,
  isin,
  onShowModalChange,
}: fundProps) {
  const [showModal, setShowModal] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<SuitabilitySelectedAnswer[]>([]);
  const selectedAnswersRef = useRef<SuitabilitySelectedAnswer[]>([]);
  const answersByQuestionRef = useRef<Map<number, SuitabilitySelectedAnswer>>(new Map());
  const [showResult, setShowResult] = useState(false);
  const [isMatch, setIsMatch] = useState(false);
  const [leadAuthToken, setLeadAuthToken] = useState<string | undefined>("");
  const [revealedQuestionsCount, setRevealedQuestionsCount] = useState(0);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [typingDoneFlags, setTypingDoneFlags] = useState<boolean[]>([]);
  const [userDetails, setUserDetails] = useState<SuitabilityUserDetails>(EMPTY_USER_DETAILS);
  const [showFormStep, setShowFormStep] = useState(false);
  const [questions, setQuestions] = useState<SuitabilityQuestion[]>([]);
  const [subject, setSubject] = useState<string>("");
  const [showLottie, setShowLottie] = useState(false);
  const [fundsReturn, setFundsReturn] = useState<SuitabilityBestReturn>({
    year: "",
    value: 0,
  });
  const [resultPoints, setResultPoints] = useState<string[]>([]);

  const pathname = usePathname();
  const dispatch = useDispatch();
  const stepRef = useRef<HTMLDivElement>(null);

  const { isLoggedIn } = useLoginModal();

  useEffect(() => {
    if (onShowModalChange) {
      onShowModalChange(showModal);
    }
  }, [showModal, onShowModalChange]);

  useEffect(() => {
    if (showModal) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }
  }, [showModal]);

  useFocusTrap(stepRef);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res: SuitabilityApiResponse = await apiPost("feedback/getQuestion", {
          subject_group: "Suitability Web",
        });

        if (!res.status) throw new Error(res.message || "Something went wrong");

        const { subjectKey, questions: finalQuestions } = formatQuestions(res);
        setSubject(subjectKey);
        setQuestions(finalQuestions);
      } catch (error) {
        console.error(error || "Something went wrong");
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }, []);

  const resetTest = () => {
    setSelectedAnswers([]);
    setTypingDoneFlags([]);
    setShowResult(false);
    setIsMatch(false);
    setRevealedQuestionsCount(0);
    setShowFormStep(false);
    setTyping(true);
    setUserDetails(EMPTY_USER_DETAILS);
    selectedAnswersRef.current = [];
    answersByQuestionRef.current.clear();
    setTimeout(() => {
      setTyping(false);
      setRevealedQuestionsCount(1);
      scrollToBottom();
    }, 1000);
  };

  const handleClose = () => {
    setShowModal(false);
    setTimeout(() => {
      resetTest();
    }, 200);
  };

  const handleShowResult = async () => {
    if (!showLottie) setShowLottie(true);

    const answerList = Array.from(answersByQuestionRef.current.values())
      .filter((ans) => ans && ans.questionId >= 0 && typeof ans.optionId !== "undefined")
      .map((ans) => {
        const q = questions.find((qq) => qq.id === ans.questionId);
        const opt = q?.options.find((o) => o.id === ans.optionId);
        return {
          question_id: ans.questionId,
          option_id: ans.optionId as number,
          answer: ans.answer || opt?.label || "",
        };
      });

    const payload = {
      subject,
      answerList,
      device: "Web",
      schemeData: {
        scheme_code: schemeCode,
        scheme_category_id: schemeCatId,
        isin,
      },
    };

    try {
      if (!isLoggedIn && !leadAuthToken) {
        console.error("Skipping feedback API — no leadAuthToken yet");
        return;
      }

      const res = await apiPost("feedback/submitFeedback", payload, isLoggedIn ? {} : { authToken: leadAuthToken });

      if (!res.status) throw new Error(res.message || "Something went wrong");

      const fundSuitable = res.fund_suitable;

      setIsMatch(fundSuitable.suitable);
      setShowLottie(false);
      setResultPoints(fundSuitable.suitable ? fundSuitable.successPoints : fundSuitable.failiurPoints);

      const sipReturn = fundSuitable.sip_return || {};

      const bestReturn: SuitabilityBestReturn = sipReturn.five_year
        ? { year: "5", value: sipReturn.five_year }
        : sipReturn.three_year
          ? { year: "3", value: sipReturn.three_year }
          : sipReturn.two_year
            ? { year: "2", value: sipReturn.two_year }
            : sipReturn.one_year_abs
              ? { year: "1", value: sipReturn.one_year_abs }
              : { year: "", value: null };

      setFundsReturn(bestReturn);
      setShowResult(true);
      scrollToBottom();
    } catch (error) {
      console.error("Something went wrong!", error);
    }
  };

  useEffect(() => {
    if (!isLoggedIn && leadAuthToken) {
      setTimeout(() => {
        handleShowResult();
      }, 1000);
    }
  }, [isLoggedIn, leadAuthToken]);

  const handleFormSubmit = async () => {
    setShowLottie(true);

    if (!userDetails.name || !userDetails.phone || !userDetails.email) {
      return;
    }

    const payload = {
      mobile: userDetails.phone,
      name: userDetails.name,
      email: userDetails.email,
      page: pathname,
    };

    try {
      const res = await apiPost("web/leadFromWeb", payload);

      if (!res.status) throw new Error(res.message || "Something went wrong");

      setLeadAuthToken(res.token);
    } catch (error) {
      console.error("Something went wrong", error);
    }
  };

  const handleAnswer = (index: number, optionId: number) => {
    const selectedOption = questions[index]?.options.find((o) => o.id === optionId);
    if (!selectedOption) return;

    if (questions[index]?.isIntro && selectedOption.type === "close") {
      handleClose();
      return;
    }

    const answer: SuitabilitySelectedAnswer = {
      questionId: questions[index].id,
      optionId: selectedOption.id,
      answer: selectedOption.label || "",
    };

    setSelectedAnswers((prev) => {
      const updated = [...prev];
      updated[index] = answer;
      selectedAnswersRef.current = updated;
      return updated;
    });

    // Persist answer synchronously by question id to avoid async state races
    answersByQuestionRef.current.set(answer.questionId, answer);

    setTyping(true);

    if (index < questions.length - 1) {
      setTimeout(() => {
        setTyping(false);
        setRevealedQuestionsCount((prev) => {
          const newCount = prev + 1;
          setTypingDoneFlags((prevFlags) => {
            const updatedFlags = [...prevFlags];
            updatedFlags[newCount - 1] = false;
            return updatedFlags;
          });
          return newCount;
        });
        scrollToBottom();
      }, 1200);
    } else {
      setTimeout(() => {
        setTyping(false);
        if (isLoggedIn) {
          handleShowResult();
        } else {
          setShowFormStep(true);
        }
        scrollToBottom();
      }, 1500);
    }
  };

  const handleAddToCart = async () => {
    const payload = {
      scheme_code: schemeCode || "",
      inv_type: "Lumpsum",
      amount: "10000",
      sip_date: "",
      offset: "",
      addnl_investment: "",
      folio_no: "",
      source: pathname,
      medium: "Web",
      folio: "",
    };

    try {
      const res: CartAPIResponse = await apiPost("addToCart", payload);
      if (res.status) {
        dispatch(increaseCart());
        addToast({
          title: "Add To Cart",
          description: "Item added to cart!",
          status: "success",
        });
        handleClose();
      } else {
        addToast({
          title: "Add To Cart",
          description: "Failed to add to cart!",
          status: "failed",
        });
      }
    } catch (err) {
      console.error(err);
      addToast({
        title: "Add To Cart",
        description: "Something Went Wrong!",
        status: "failed",
      });
    }
  };

  const startModal = () => {
    loadSuitabilityLoader();
    setShowModal(true);
    setTimeout(() => {
      resetTest();
    }, 100);
  };

  const getTitle = () => {
    if (showResult) return "Your Suitability";
    if (!isLoggedIn && showFormStep) return "Your Details";
    if (revealedQuestionsCount <= 1) return "Let's Get Started.";
    if (revealedQuestionsCount === 2) return "Investment Style";
    if (revealedQuestionsCount === 3) return "Return Goals";
    if (revealedQuestionsCount === 4) return "Risk Comfort";
  };

  const markTypingDone = useCallback(
    (index: number) =>
      setTypingDoneFlags((prev) => {
        const updated = [...prev];
        updated[index] = true;
        scrollToBottom();
        return updated;
      }),
    [scrollToBottom],
  );

  const handleFormBubbleComplete = useCallback(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }, [scrollToBottom]);

  const progressTotal = questions.length + (isLoggedIn ? 1 : 2);

  return (
    <>
      <button
        onClick={startModal}
        className="w-full flex items-center justify-center gap-1.5 mt-6 border-2 border-[#1A51AA59] text-white font-semibold text-sm xs:text-base p-3 rounded-xl shadow-md bg-[radial-gradient(50%_50%_at_50%_50%,#1A51AA_0%,#083E95_100%)] shine-button-hover"
      >
        Check your Suitability here
        <icon.rightArrow className="w-5 h-5 xs:w-6 xs:h-6 shrink-0 text-white" />
      </button>

      <div
        ref={stepRef}
        className={`fixed inset-0 z-5500 xs:z-50 flex items-center justify-center backdrop-blur-lg bg-black/60 text-left transition-all duration-300 ${showModal ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"}`}
        onClick={handleClose}
      >
        <div
          className="flex flex-col xs:rounded-4xl bg-primaryBlue w-full h-full xs:max-w-149 xs:h-136.25 p-4 xs:p-5 shadow-xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 mb-4.5">
            <icon.logoIcon className="w-5.75 xs:w-8.25 h-4.5 xs:h-7" />

            <strong className="text-white text-base xs:text-1.5xl leading-8">Fund Suitability Test</strong>

            {showResult && (
              <button
                onClick={resetTest}
                className="text-xs xs:text-2xs font-medium border border-white text-white rounded-full ml-auto py-0.5 px-1.5 xs:px-2.5 shrink-0 transition duration-350 hover:text-primaryBlue hover:bg-white"
              >
                Retake Test
              </button>
            )}

            <button
              onClick={handleClose}
              className={`w-6 h-6 inline-flex justify-center items-center transition-all duration-300 hover:scale-105 hover:bg-white/15 rounded-full text-white shrink-0 ${showResult ? null : "ml-auto"}`}
              aria-label="Close Button"
            >
              <icon.popupCloseIcon stroke="#ffffff" />
            </button>
          </div>

          <div className="mb-5 text-white text-base font-medium">
            {getTitle()}{" "}
            <SuitabilityProgressBar
              total={progressTotal}
              completed={showResult ? progressTotal : revealedQuestionsCount}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              ref={scrollRef}
              key={showResult ? "result" : "questions"}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="bg-white h-full overflow-y-auto scrollbar-none rounded-4xl"
            >
              {showLottie ? (
                <SuitabilityLoader />
              ) : !showResult ? (
                <div className="space-y-8 py-3 px-5 xs:py-4 xs:px-9">
                  {questions.slice(0, revealedQuestionsCount).map((q, i) => {
                    const selectedOption = selectedAnswers[i]
                      ? q.options.find((opt) => opt.id === selectedAnswers[i]?.optionId)!
                      : undefined;

                    return (
                      <Fragment key={i}>
                        <SuitabilityChatBubble message={q.question} onComplete={() => markTypingDone(i)} />

                        {selectedOption ? (
                          <SuitabilitySelectedCard option={selectedOption} />
                        ) : typingDoneFlags[i] ? (
                          <SuitabilityQuestionOptions question={q} onSelect={(optionId) => handleAnswer(i, optionId)} />
                        ) : null}
                      </Fragment>
                    );
                  })}

                  {typing && <SuitabilityTypingBubble />}

                  {showFormStep && (
                    <>
                      <SuitabilityChatBubble
                        message="Before showing your result, tell us a bit about you:"
                        onComplete={handleFormBubbleComplete}
                      />
                      <SuitabilityDetailsForm
                        userDetails={userDetails}
                        setUserDetails={setUserDetails}
                        onSubmit={handleFormSubmit}
                      />
                    </>
                  )}
                </div>
              ) : (
                <SuitabilityResultCard
                  isMatch={isMatch}
                  handleCartButton={handleAddToCart}
                  name={name}
                  imgCode={imgCode}
                  points={resultPoints}
                  bestReturn={fundsReturn}
                  closeModal={handleClose}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}
